/**
 * In-page replacements for the browser's own confirm() / prompt() on classic pages.
 *
 * Why: the owner's rule is "no web-style popups" (2026-10-01). The browser dialogs are the worst of the
 * kind — grey OS chrome, blocking, and inside the iOS app they read as a crash. Classic manager pages that
 * now open inside the store app (v2/manager/lib/app/appClassic.ts) used them for deletes, rejection
 * reasons and completion costs. This module gives the same one-line call sites an awaited, in-page
 * dialog in the page's own (classic) look:
 *
 *   if (await inPageDialog.confirm("Delete this vendor?", { destructive: true })) …
 *   const reason = await inPageDialog.prompt("Rejection reason");   // null = cancelled
 *
 * ONE host renders it (<InPageDialogHost/>, mounted once in App next to the Toaster), so a call site
 * needs no hook and no element. One dialog at a time; asking again while one is open cancels the first
 * (resolves false / null). With no host mounted (a unit test, a preview), every ask resolves as Cancel —
 * never a silent "yes".
 */
export type ConfirmOpts = { body?: string; confirmLabel?: string; destructive?: boolean };
export type PromptOpts = { body?: string; initial?: string; confirmLabel?: string; inputMode?: "decimal" | "text" | "tel" };

export type Ask =
  | ({ kind: "confirm"; title: string; resolve: (v: boolean) => void } & ConfirmOpts)
  | ({ kind: "prompt"; title: string; resolve: (v: string | null) => void } & PromptOpts);

let current: Ask | null = null;
const listeners = new Set<(a: Ask | null) => void>();

function cancel(a: Ask) {
  if (a.kind === "confirm") a.resolve(false);
  else a.resolve(null);
}
function show(a: Ask) {
  if (current) cancel(current);
  if (listeners.size === 0) { cancel(a); return; }
  current = a;
  listeners.forEach((l) => l(a));
}
/** Settle the open ask: ok=false is Cancel. Exported for the host and the unit test. */
export function settleInPageDialog(ok: boolean, value?: string) {
  const a = current;
  current = null;
  listeners.forEach((l) => l(null));
  if (!a) return;
  if (a.kind === "confirm") a.resolve(ok);
  else a.resolve(ok ? (value ?? "") : null);
}
/** Test-only: attach a listener as if a host were mounted. */
export function subscribeInPageDialog(l: (a: Ask | null) => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export const inPageDialog = {
  confirm(title: string, opts: ConfirmOpts = {}): Promise<boolean> {
    return new Promise((resolve) => show({ kind: "confirm", title, ...opts, resolve }));
  },
  prompt(title: string, opts: PromptOpts = {}): Promise<string | null> {
    return new Promise((resolve) => show({ kind: "prompt", title, ...opts, resolve }));
  },
};

